/**
 * Issue Types Hook
 *
 * React Query hook for fetching dynamic issue type definitions.
 * Provides a label lookup helper that falls back to slug formatting.
 */

import { useQuery } from "@tanstack/react-query";
import { fetchIssueTypes } from "@/data/api/issue-types";

export const ISSUE_TYPES_QUERY_KEYS = {
    all: ["issue-types"] as const,
    list: (category?: string) => [...ISSUE_TYPES_QUERY_KEYS.all, category] as const,
};

export function useIssueTypes(category?: string) {
    return useQuery({
        queryKey: ISSUE_TYPES_QUERY_KEYS.list(category),
        queryFn: () => fetchIssueTypes(category),
        staleTime: 5 * 60 * 1000, // 5 minutes — types rarely change
    });
}

/**
 * Returns a lookup function that resolves a slug to its human label.
 * Falls back to formatting the slug (replace _ with space, title case).
 */
export function useIssueTypeLabel(): (slug: string) => string {
    const { data: types } = useIssueTypes();

    const labelMap = new Map<string, string>();
    if (types) {
        for (const t of types) {
            labelMap.set(t.slug, t.label);
        }
    }

    return (slug: string) =>
        labelMap.get(slug) ??
        slug.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

export type { IssueType } from "@/data/api/issue-types";
